import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, Banknote, Briefcase, CalendarDays, CheckCircle2, Clock3, Eye, FileText, Wallet } from 'lucide-react';
import { getApiUrl } from '../../config/api';
import { formatCurrency } from '../../utils/payrollCalculations';

interface PayrollPeriod {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
}

interface PayslipRecord {
  id: number;
  periodId: number;
  baseSalary: number;
  housingAllowance: number;
  transportAllowance: number;
  responsibilityAllowance: number;
  otherAllowances: number;
  grossSalary: number;
  totalDeductions: number;
  netSalary: number;
  napsaDeduction: number;
  payeDeduction: number;
  nhimaDeduction: number;
  status: string;
  paymentDate: string | null;
  createdAt: string;
  period?: PayrollPeriod;
}

interface PayrollProfile {
  staffId?: number;
  teacherId?: number;
  name?: string;
  email?: string;
  baseSalary?: number;
  housingAllowance?: number;
  transportAllowance?: number;
  responsibilityAllowance?: number;
  otherAllowances?: number;
  status?: string;
}

const statusLabelMap: Record<string, string> = {
  generated: 'Pending',
  approved: 'Approved',
  paid: 'Paid',
  cancelled: 'Cancelled'
};

const statusBadgeMap: Record<string, string> = {
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  approved: 'bg-sky-50 text-sky-700 border-sky-100',
  generated: 'bg-amber-50 text-amber-700 border-amber-100',
  cancelled: 'bg-rose-50 text-rose-700 border-rose-100'
};

const MyPayroll: React.FC = () => {
  const navigate = useNavigate();
  const [payslips, setPayslips] = useState<PayslipRecord[]>([]);
  const [profile, setProfile] = useState<PayrollProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token') || '';
        const headers = { Authorization: `Bearer ${token}` };
        const [payslipsResponse, profileResponse] = await Promise.allSettled([
          axios.get(getApiUrl('payroll/my-payslips'), { headers }),
          axios.get(getApiUrl('payroll/my-payroll'), { headers })
        ]);

        if (payslipsResponse.status === 'fulfilled') {
          const rawData = Array.isArray(payslipsResponse.value.data) ? payslipsResponse.value.data : [];
          const mappedData = rawData.map((p: any) => ({
            ...p,
            period: p.period || {
              id: p.periodId,
              name: p.periodName || p.periodname || p.payPeriod || 'Unknown Period',
              startDate: p.startDate || p.startdate || '',
              endDate: p.endDate || p.enddate || ''
            }
          }));
          setPayslips(mappedData);
        } else {
          throw payslipsResponse.reason;
        }

        if (profileResponse.status === 'fulfilled') {
          const profileData = profileResponse.value.data;
          setProfile(profileData && !profileData.message ? {
            ...profileData,
            staffId: profileData.staffId || profileData.teacherId
          } : null);
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Error fetching payroll data');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const availableYears = useMemo(() => {
    const years = new Set<number>();
    payslips.forEach((p) => {
      const year = p.period?.startDate ? new Date(p.period.startDate).getFullYear() : new Date(p.createdAt).getFullYear();
      years.add(year);
    });
    if (years.size === 0) years.add(new Date().getFullYear());
    return Array.from(years).sort((a, b) => b - a);
  }, [payslips]);

  const filteredPayslips = useMemo(() => {
    return payslips
      .filter((p) => {
        const payslipYear = p.period?.startDate ? new Date(p.period.startDate).getFullYear() : new Date(p.createdAt).getFullYear();
        const statusMatch = filterStatus === 'all' || p.status === filterStatus;
        return payslipYear === selectedYear && statusMatch;
      })
      .sort((a, b) => {
        const dateA = a.period?.startDate ? new Date(a.period.startDate).getTime() : new Date(a.createdAt).getTime();
        const dateB = b.period?.startDate ? new Date(b.period.startDate).getTime() : new Date(b.createdAt).getTime();
        return dateB - dateA;
      });
  }, [payslips, selectedYear, filterStatus]);

  const summary = useMemo(() => {
    return {
      totalEarnings: filteredPayslips.reduce((sum, p) => sum + Number(p.grossSalary || 0), 0),
      totalDeductions: filteredPayslips.reduce((sum, p) => sum + Number(p.totalDeductions || 0), 0),
      totalNetSalary: filteredPayslips.reduce((sum, p) => sum + Number(p.netSalary || 0), 0),
      paidCount: filteredPayslips.filter((p) => p.status === 'paid').length,
      approvedCount: filteredPayslips.filter((p) => p.status === 'approved').length,
      pendingCount: filteredPayslips.filter((p) => p.status === 'generated').length,
      totalPayslips: filteredPayslips.length
    };
  }, [filteredPayslips]);

  const currentPackage = useMemo(() => {
    if (!profile) return null;
    const baseSalary = Number(profile.baseSalary || 0);
    const housing = Number(profile.housingAllowance || 0);
    const transport = Number(profile.transportAllowance || 0);
    const responsibility = Number(profile.responsibilityAllowance || 0);
    const other = Number(profile.otherAllowances || 0);
    const allowances = housing + transport + responsibility + other;

    return {
      baseSalary,
      housing,
      transport,
      responsibility,
      other,
      allowances,
      grossEstimate: baseSalary + allowances
    };
  }, [profile]);

  const latestPayslip = filteredPayslips[0] || payslips[0] || null;

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZM', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPayslipMonth = (payslip: PayslipRecord) => {
    const sourceDate =
      payslip.paymentDate ||
      payslip.period?.endDate ||
      payslip.period?.startDate ||
      payslip.createdAt;

    if (!sourceDate) return 'Payroll Month';

    return new Date(sourceDate).toLocaleDateString('en-ZM', {
      month: 'long',
      year: 'numeric'
    });
  };

  const handleViewPayslip = (payslipId: number) => {
    navigate(`/my-payslip/${payslipId}`);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="mt-4 text-gray-600">Loading payroll data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/40 p-4 md:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-col gap-3 rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">Payroll Workspace</p>
            <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-900">My Payroll</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-500">
              Review your current salary package, track payslip history, and open any approved or paid payslip in full detail.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              <span className="font-semibold text-slate-900">{summary.totalPayslips}</span> payslip{summary.totalPayslips === 1 ? '' : 's'} in {selectedYear}
            </div>
            {profile?.status && (
              <div className={`rounded-2xl border px-4 py-3 text-sm font-semibold ${statusBadgeMap[profile.status] || 'border-slate-200 bg-slate-50 text-slate-700'}`}>
                Payroll profile: {statusLabelMap[profile.status] || profile.status}
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <article className="rounded-[2rem] border border-slate-100 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Gross Earnings</p>
                <p className="mt-3 text-3xl font-black tracking-tight text-emerald-600">{formatCurrency(summary.totalEarnings)}</p>
                <p className="mt-2 text-sm text-slate-500">Total earnings for {selectedYear}</p>
              </div>
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-3 text-emerald-700">
                <Wallet size={18} />
              </div>
            </div>
          </article>
          <article className="rounded-[2rem] border border-slate-100 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Deductions</p>
                <p className="mt-3 text-3xl font-black tracking-tight text-rose-600">{formatCurrency(summary.totalDeductions)}</p>
                <p className="mt-2 text-sm text-slate-500">Combined deductions in {selectedYear}</p>
              </div>
              <div className="rounded-2xl border border-rose-100 bg-rose-50 p-3 text-rose-700">
                <AlertCircle size={18} />
              </div>
            </div>
          </article>
          <article className="rounded-[2rem] border border-slate-100 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Net Pay</p>
                <p className="mt-3 text-3xl font-black tracking-tight text-sky-600">{formatCurrency(summary.totalNetSalary)}</p>
                <p className="mt-2 text-sm text-slate-500">Net salary across filtered payslips</p>
              </div>
              <div className="rounded-2xl border border-sky-100 bg-sky-50 p-3 text-sky-700">
                <Banknote size={18} />
              </div>
            </div>
          </article>
          <article className="rounded-[2rem] border border-slate-100 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Payment Status</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-xs font-black uppercase tracking-wide text-emerald-700">
                    {summary.paidCount} Paid
                  </span>
                  <span className="rounded-full border border-sky-100 bg-sky-50 px-3 py-1 text-xs font-black uppercase tracking-wide text-sky-700">
                    {summary.approvedCount} Approved
                  </span>
                  <span className="rounded-full border border-amber-100 bg-amber-50 px-3 py-1 text-xs font-black uppercase tracking-wide text-amber-700">
                    {summary.pendingCount} Pending
                  </span>
                </div>
                <p className="mt-3 text-sm text-slate-500">Status of filtered payroll records</p>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-slate-50 p-3 text-slate-700">
                <CheckCircle2 size={18} />
              </div>
            </div>
          </article>
        </div>

        <div className="grid gap-6 xl:grid-cols-[1.4fr_0.9fr]">
          <section className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm">
            <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h2 className="text-xl font-black tracking-tight text-slate-900">Payslip History</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Filter by year and payroll status, then open a full payslip for detailed earnings and deductions.
                </p>
              </div>
              <div className="flex flex-wrap gap-4">
                <div>
                  <label className="mb-1 block text-xs font-black uppercase tracking-[0.18em] text-slate-400">Year</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  >
                    {availableYears.map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-black uppercase tracking-[0.18em] text-slate-400">Status</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  >
                    <option value="all">All Status</option>
                    <option value="generated">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="paid">Paid</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>

            {filteredPayslips.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm">
                  <FileText size={28} />
                </div>
                <h3 className="mt-4 text-lg font-bold text-slate-900">No payslips found</h3>
                <p className="mt-2 text-sm text-slate-500">
                  There are no payroll records for the selected year and status. Try a different filter or wait for the next payroll cycle.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-slate-100">
                <table className="min-w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-black uppercase tracking-[0.15em] text-slate-500">Period</th>
                      <th className="px-4 py-3 text-left text-xs font-black uppercase tracking-[0.15em] text-slate-500">Gross</th>
                      <th className="px-4 py-3 text-left text-xs font-black uppercase tracking-[0.15em] text-slate-500">Deductions</th>
                      <th className="px-4 py-3 text-left text-xs font-black uppercase tracking-[0.15em] text-slate-500">Net</th>
                      <th className="px-4 py-3 text-left text-xs font-black uppercase tracking-[0.15em] text-slate-500">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-black uppercase tracking-[0.15em] text-slate-500">Paid On</th>
                      <th className="px-4 py-3 text-right text-xs font-black uppercase tracking-[0.15em] text-slate-500">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {filteredPayslips.map((payroll) => (
                      <tr key={payroll.id} className="transition hover:bg-slate-50/60">
                        <td className="px-4 py-4">
                          <p className="font-semibold text-slate-900">{formatPayslipMonth(payroll)}</p>
                          <p className="text-sm text-slate-500">
                            {payroll.paymentDate
                              ? `Paid on ${formatDate(payroll.paymentDate)}`
                              : `Generated on ${formatDate(payroll.createdAt)}`}
                          </p>
                        </td>
                        <td className="px-4 py-4 text-sm font-semibold text-emerald-600">{formatCurrency(Number(payroll.grossSalary || 0))}</td>
                        <td className="px-4 py-4 text-sm font-semibold text-rose-600">({formatCurrency(Number(payroll.totalDeductions || 0))})</td>
                        <td className="px-4 py-4 text-sm font-black text-sky-600">{formatCurrency(Number(payroll.netSalary || 0))}</td>
                        <td className="px-4 py-4">
                          <span className={`rounded-full border px-3 py-1 text-xs font-black uppercase tracking-wide ${statusBadgeMap[payroll.status] || 'border-slate-200 bg-slate-50 text-slate-700'}`}>
                            {statusLabelMap[payroll.status] || payroll.status}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-slate-600">{payroll.paymentDate ? formatDate(payroll.paymentDate) : '-'}</td>
                        <td className="px-4 py-4 text-right">
                          <button
                            onClick={() => handleViewPayslip(payroll.id)}
                            className="inline-flex items-center gap-2 rounded-xl border border-sky-100 bg-sky-50 px-3 py-2 text-sm font-semibold text-sky-700 transition hover:bg-sky-100"
                          >
                            <Eye size={16} />
                            View Payslip
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <div className="space-y-6">
            <section className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-black tracking-tight text-slate-900">Current Payroll Profile</h2>
                  <p className="mt-1 text-sm text-slate-500">Your configured monthly package and payroll setup.</p>
                </div>
                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-3 text-slate-700">
                  <Briefcase size={18} />
                </div>
              </div>

              {!currentPackage ? (
                <div className="mt-5 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
                  <p className="text-sm font-semibold text-slate-700">No active payroll profile found.</p>
                  <p className="mt-2 text-sm text-slate-500">Your school admin needs to configure your payroll package before current compensation details can appear here.</p>
                </div>
              ) : (
                <div className="mt-5 space-y-4">
                  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">Estimated Gross Package</p>
                    <p className="mt-2 text-3xl font-black tracking-tight text-slate-900">{formatCurrency(currentPackage.grossEstimate)}</p>
                    <p className="mt-2 text-sm text-slate-500">{profile?.name || 'Staff member'} • {profile?.email || 'Email not available'}</p>
                  </div>
                  <div className="space-y-3">
                    {[
                      { label: 'Base Salary', value: currentPackage.baseSalary },
                      { label: 'Housing Allowance', value: currentPackage.housing },
                      { label: 'Transport Allowance', value: currentPackage.transport },
                      { label: 'Responsibility Allowance', value: currentPackage.responsibility },
                      { label: 'Other Allowances', value: currentPackage.other }
                    ].map((entry) => (
                      <div key={entry.label} className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3">
                        <span className="text-sm font-medium text-slate-600">{entry.label}</span>
                        <span className="text-sm font-bold text-slate-900">{formatCurrency(entry.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            <section className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-black tracking-tight text-slate-900">Latest Payslip Snapshot</h2>
                  <p className="mt-1 text-sm text-slate-500">Quick summary of your most recent payroll record.</p>
                </div>
                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-3 text-slate-700">
                  <CalendarDays size={18} />
                </div>
              </div>

              {!latestPayslip ? (
                <div className="mt-5 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
                  <p className="text-sm font-semibold text-slate-700">No payslip history yet.</p>
                  <p className="mt-2 text-sm text-slate-500">Once payroll is generated for you, the latest payslip summary will appear here.</p>
                </div>
              ) : (
                <div className="mt-5 space-y-4">
                  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">{formatPayslipMonth(latestPayslip)}</p>
                    <div className="mt-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Net Salary</p>
                        <p className="text-2xl font-black tracking-tight text-sky-600">{formatCurrency(Number(latestPayslip.netSalary || 0))}</p>
                      </div>
                      <span className={`rounded-full border px-3 py-1 text-xs font-black uppercase tracking-wide ${statusBadgeMap[latestPayslip.status] || 'border-slate-200 bg-slate-50 text-slate-700'}`}>
                        {statusLabelMap[latestPayslip.status] || latestPayslip.status}
                      </span>
                    </div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-xl border border-slate-100 px-4 py-3">
                      <p className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">Gross</p>
                      <p className="mt-2 text-lg font-bold text-emerald-600">{formatCurrency(Number(latestPayslip.grossSalary || 0))}</p>
                    </div>
                    <div className="rounded-xl border border-slate-100 px-4 py-3">
                      <p className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">Deductions</p>
                      <p className="mt-2 text-lg font-bold text-rose-600">{formatCurrency(Number(latestPayslip.totalDeductions || 0))}</p>
                    </div>
                    <div className="rounded-xl border border-slate-100 px-4 py-3">
                      <p className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">Payment Date</p>
                      <p className="mt-2 text-sm font-semibold text-slate-900">{latestPayslip.paymentDate ? formatDate(latestPayslip.paymentDate) : 'Not paid yet'}</p>
                    </div>
                    <div className="rounded-xl border border-slate-100 px-4 py-3">
                      <p className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">Record Created</p>
                      <p className="mt-2 text-sm font-semibold text-slate-900">{formatDate(latestPayslip.createdAt)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleViewPayslip(latestPayslip.id)}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                  >
                    <Eye size={16} />
                    Open Latest Payslip
                  </button>
                </div>
              )}
            </section>

            <section className="rounded-[2rem] border border-blue-100 bg-blue-50 p-5 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="rounded-2xl bg-white p-3 text-blue-600 shadow-sm">
                  <Clock3 size={18} />
                </div>
                <div>
                  <h3 className="text-base font-black tracking-tight text-blue-900">Payroll Notes</h3>
                  <ul className="mt-3 space-y-2 text-sm text-blue-800">
                    <li>- Approved payslips are ready for payment processing.</li>
                    <li>- Paid payslips include the actual payment date in your history.</li>
                    <li>- Use the payslip detail page for a full earnings and deductions breakdown.</li>
                  </ul>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyPayroll;
